/**
 * A trial-code link that has to go through a sign-in first (subscription R19).
 *
 * The shell's guard sends a signed-out visitor of `/konto?code=PGB` to `/sign-in`, and the app
 * carries no return-to address through that, a sign-up or a profile completion. So the guard PARKS
 * the code here on the way out, and the first time a signed-in session enters the shell the code is
 * claimed and the trainer is sent to the Subskrypcja tab with it — once, then forgotten.
 *
 * `localStorage` rather than `sessionStorage`: a sign-up's confirmation e-mail opens a new tab, and
 * that tab is where the trainer arrives signed in. What the record means, and when it is too old to
 * mean anything, is `model/trialCode`'s; this file is the store and nothing else.
 */

import { preferenceStore } from '~/shared/lib/preferenceStore';

import { normalizeTrialCode, parkedTrialCode, parkTrialCode } from '../model/trialCode';

const PARKED_KEY = 'movebuddy.trialCode.parked';

/** The one address that reads a trial code. */
const ACCOUNT_PATH = '/konto';

function codeIn(pathname: string, search: Readonly<Record<string, unknown>>): string | null {
  return pathname === ACCOUNT_PATH ? normalizeTrialCode(search.code) : null;
}

/** On the way to `/sign-in`: keep the code the address carries, if it carries one. */
export function parkTrialCodeFrom(pathname: string, search: Readonly<Record<string, unknown>>): void {
  const code = codeIn(pathname, search);
  if (code === null) return;
  try {
    preferenceStore()?.setItem(PARKED_KEY, parkTrialCode(code, Date.now()));
  } catch {
    /* A quota or a blocked store: the link then needs opening again after the sign-in, as before. */
  }
}

/**
 * On a signed-in entry into the shell: the parked code, taken out of the store, or `null`.
 *
 * An address that already carries a code wins and the parked one is dropped — it is the same link
 * arriving the ordinary way, or a newer one.
 */
export function claimParkedTrialCode(
  pathname: string,
  search: Readonly<Record<string, unknown>>,
): string | null {
  const store = preferenceStore();
  if (store === null) return null;
  let stored: string | null;
  try {
    stored = store.getItem(PARKED_KEY);
    if (stored !== null) store.removeItem(PARKED_KEY);
  } catch {
    return null;
  }
  const code = parkedTrialCode(stored, Date.now());
  return code === null || codeIn(pathname, search) !== null ? null : code;
}
